import * as React from 'react';
import { Layout } from 'react-admin';
import { alpha } from '@mui/material';
import { sidebarColors } from './appBarTheme';
import AppBar from './AppBar';
import Menu from './Menu';
import ForcePasswordChange from '../screens/change-password/ForcePasswordChange';

/**
 * Whether this session still owes a password change.
 *
 * Two ways it becomes true, and both matter:
 *
 *  - login said so, before this component ever mounted;
 *  - a request came back 428 while the panel was open, because an
 *    administrator reset this account's password in the meantime. The flag is
 *    written by checkError() and announced with an event, since writing to
 *    localStorage re-renders nothing and the browser's own "storage" event
 *    fires only in other tabs.
 */
const useMustChangePassword = () => {
    const read = () => localStorage.getItem('must_change_password') === '1';
    const [must, setMust] = React.useState(read);

    React.useEffect(() => {
        const sync = () => setMust(read());
        window.addEventListener('wizi-must-change-password', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('wizi-must-change-password', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    return must;
};

/**
 * The panel, or -- for an account still on a password somebody else chose --
 * the form that fixes that, and nothing else.
 *
 * The menu and the app bar go too. Leaving them would offer a school teacher
 * their class lists while every one of those requests is being refused with
 * 428, which reads as a broken product rather than as a step to complete.
 *
 * This is presentation. The refusal itself is
 * API_Controller::enforce_password_change(), server side, where it cannot be
 * skipped by anyone editing localStorage.
 */
export default ({ children }: { children: React.ReactNode }) => {
    if (useMustChangePassword()) {
        return <ForcePasswordChange />;
    }

    return (
        <Layout
            appBar={AppBar}
            menu={Menu}
            /**
             * The sidebar band is painted here, on the drawer itself.
             *
             * It used to be a fixed '#02384d' in App.css. Moving it into the
             * Menu component alone was not enough: react-admin's sidebar paper
             * takes its background from the theme, and several bundled themes
             * override it, so the band has to be set on the drawer with a
             * selector that outranks them. Colours are still derived from the
             * active theme -- see appBarTheme.ts -- so the swapper keeps
             * working.
             */
            sx={theme => {
                const { top, bottom, fg } = sidebarColors(theme);
                const band = `linear-gradient(180deg, ${top} 0%, ${alpha(bottom, 0.92)} 100%)`;
                return {
                    '& .MuiDrawer-docked, & .MuiDrawer-docked .MuiPaper-root': {
                        backgroundColor: top,
                        backgroundImage: band,
                        borderRight: 'none',
                        color: fg,
                    },
                    // Beats the per-theme RaMenuItemLink overrides, which paint
                    // their own active state for a light sidebar and are
                    // unreadable on this band.
                    '& .MuiDrawer-docked .RaMenuItemLink-active': {
                        backgroundColor: alpha(fg, 0.18),
                        backgroundImage: 'none',
                        color: fg,
                        borderLeft: 'none',
                        boxShadow: 'none',
                    },
                };
            }}
        >
            {children}
        </Layout>
    );
};
